import React, { useEffect, useState } from 'react';
import { api } from '@/lib/db';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Card, Button, Input } from '@/components/ui/Components';
import SafeIcon from '@/common/SafeIcon';

const Settings = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [business, setBusiness] = useState(null);
  const [notifEmail, setNotifEmail] = useState('');
  const [savingNotif, setSavingNotif] = useState(false);

  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      const data = await api.getBusinessById(user.business_id);
      setBusiness(data);
      setNotifEmail(data.notification_email || '');
    };
    fetch();
  }, [user.business_id]);

  const handleSaveNotif = async (e) => {
    e.preventDefault();
    setSavingNotif(true);
    try {
      await api.updateBusinessData(user.business_id, { notification_email: notifEmail });
      addToast('Értesítési e-mail mentve!', 'success');
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setSavingNotif(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      addToast('Az új jelszavak nem egyeznek meg', 'error');
      return;
    }
    if (pwForm.next.length < 6) {
      addToast('A jelszónak legalább 6 karakter hosszúnak kell lennie', 'error');
      return;
    }
    setSavingPw(true);
    try {
      await api.changePassword(pwForm.current, pwForm.next);
      addToast('Jelszó sikeresen megváltoztatva!', 'success');
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setSavingPw(false);
    }
  };

  if (!business) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-black text-gray-900 dark:text-white">Beállítások</h1>
        <p className="text-gray-500 dark:text-gray-400">Fiók és értesítési beállítások</p>
      </div>

      {/* ─── Vállalkozás adatai ──────────────────────────────────── */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <SafeIcon name="Briefcase" className="w-5 h-5 text-blue-600" /> Vállalkozás
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Név:</span>
            <span className="font-medium dark:text-gray-200">{business.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Helyszín limit:</span>
            <span className="font-medium dark:text-gray-200">{business.location_limit} db</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Előfizetés vége:</span>
            <span className={`font-medium ${business.expiry_date >= new Date().toISOString().split('T')[0] ? 'text-green-600' : 'text-red-600'}`}>
              {business.expiry_date}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Bejelentkezett:</span>
            <span className="font-mono text-xs dark:text-gray-200">{user.email}</span>
          </div>
        </div>
      </Card>

      {/* ─── Értesítési e-mail ──────────────────────────────────── */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
          <SafeIcon name="Bell" className="w-5 h-5 text-blue-600" /> Értesítési e-mail
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Negatív visszajelzés (1-3 csillag) esetén erre az e-mail címre küldünk értesítést.
        </p>
        <form onSubmit={handleSaveNotif} className="flex gap-3">
          <Input
            type="email"
            placeholder="ertesites@vallalkozas.hu"
            value={notifEmail}
            onChange={e => setNotifEmail(e.target.value)}
          />
          <Button type="submit" disabled={savingNotif}>
            {savingNotif ? 'Mentés...' : 'Mentés'}
          </Button>
        </form>
      </Card>

      {/* ─── Jelszó módosítás ───────────────────────────────────── */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
          <SafeIcon name="Lock" className="w-5 h-5 text-blue-600" /> Jelszó módosítása
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">A bejelentkező jelszavad megváltoztatása.</p>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <Input
            label="Jelenlegi jelszó"
            type="password"
            value={pwForm.current}
            onChange={e => setPwForm({ ...pwForm, current: e.target.value })}
            required
          />
          <Input
            label="Új jelszó (min. 6 karakter)"
            type="password"
            value={pwForm.next}
            onChange={e => setPwForm({ ...pwForm, next: e.target.value })}
            required
          />
          <Input
            label="Új jelszó megerősítése"
            type="password"
            value={pwForm.confirm}
            onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })}
            required
          />
          <div className="flex justify-end">
            <Button type="submit" disabled={savingPw}>
              {savingPw ? 'Módosítás...' : 'Jelszó módosítása'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default Settings;
